import React from "react";
import {
  Popover,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PersonIcon from "@mui/icons-material/Person";
import { useTranslation } from "react-i18next";

interface Filter {
    id: string;
    type:
      | "status"
      | "priority"
      | "due_date"
      | "start_date"
      | "updated_date"
      | "project"
      | "tags"
      | "assignee"
      | "created_by";
    value: string | string[];
    startDate?: string;
    endDate?: string;
}

interface SavedFilter {
  id: string;
  name: string;
  filters: Filter[];
  createdAt: string;
  updatedAt: string;
  project?: {
    _id: string;
    name: string;
    code: string;
  };
  createdBy?: {
    _id: string;
    userId: string;
    email: string;
    fullName: string;
  };
}

interface SavedFiltersPopoverProps {
  open: boolean;
  anchorEl: HTMLElement | null;
  onClose: () => void;
  savedFilters: SavedFilter[];
  isLoading: boolean;
  currentLoadedFilterId: string | null;
  onLoadFilter: (filter: SavedFilter) => void;
  onEditFilter: (filter: SavedFilter) => void;
  onDeleteFilter: (filterId: string) => void;
}

export const SavedFiltersPopover: React.FC<SavedFiltersPopoverProps> = ({
  open,
  anchorEl,
  onClose,
  savedFilters,
  isLoading,
  currentLoadedFilterId,
  onLoadFilter,
  onEditFilter,
  onDeleteFilter,
}) => {
  const { t } = useTranslation(["bugherd"]);
  const theme = useTheme();

  const handleLoadFilter = (savedFilter: SavedFilter) => {
    onLoadFilter(savedFilter);
    onClose();
  };

  const handleEditFilter = (savedFilter: SavedFilter) => {
    onEditFilter(savedFilter);
    onClose();
  };

  const handleDeleteFilter = async (
    filterId: string,
    event: React.MouseEvent
  ) => {
    event.stopPropagation();
    try {
      await onDeleteFilter(filterId);
    } catch (error) {
      // Error handling is done in the hook
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "left",
      }}
      PaperProps={{
        sx: {
          borderRadius: "8px",
          boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.1)",
          minWidth: 320,
          maxWidth: 450,
          maxHeight: 400,
        },
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <Box sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 2, color: "text.secondary" }}>
          {t("Saved Filters")}
        </Typography>

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <Typography sx={{ fontSize: "14px", color: "text.secondary" }}>
              {t("Loading...")}
            </Typography>
          </Box>
        ) : savedFilters.length === 0 ? (
          <Typography sx={{ fontSize: "14px", color: "text.secondary", py: 2 }}>
            {t("No saved filters")}
          </Typography>
        ) : (
          <List sx={{ p: 0, maxHeight: 300, overflow: "auto" }}>
            {savedFilters.map((savedFilter) => (
              <ListItemButton
                key={savedFilter.id}
                onClick={() => handleLoadFilter(savedFilter)}
                sx={{
                  borderRadius: "6px",
                  py: 1,
                  px: 2,
                  mb: 0.5,
                  backgroundColor:
                    currentLoadedFilterId === savedFilter.id
                      ? "rgba(25, 118, 210, 0.04)"
                      : "transparent",
                  border:
                    currentLoadedFilterId === savedFilter.id
                      ? "1px solid rgba(25, 118, 210, 0.2)"
                      : "1px solid transparent",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <BookmarkIcon
                  sx={{
                    fontSize: "16px",
                    color:
                      currentLoadedFilterId === savedFilter.id
                        ? theme.palette.primary.main
                        : theme.palette.text.secondary,
                    mr: 1,
                  }}
                />
                <ListItemText
                  primary={
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: theme.palette.text.primary,
                          fontWeight:
                            currentLoadedFilterId === savedFilter.id
                              ? 500
                              : 400,
                        }}
                      >
                        {savedFilter.name}
                      </Typography>
                      {savedFilter.project && (
                        <Chip
                          label={savedFilter.project.code}
                          size="small"
                          sx={{
                            height: "16px",
                            fontSize: "10px",
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                            color: theme.palette.text.secondary,
                          }}
                        />
                      )}
                    </Box>
                  }
                  secondary={
                    <Box sx={{ mt: 0.5 }}>
                      <Typography
                        sx={{
                          fontSize: "12px",
                          color: theme.palette.text.secondary,
                        }}
                      >
                        {savedFilter.filters.length} {t("filters")} •{" "}
                        {new Date(savedFilter.updatedAt).toLocaleDateString()}
                      </Typography>
                      {savedFilter.createdBy && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            mt: 0.5,
                          }}
                        >
                          <PersonIcon
                            sx={{
                              fontSize: "12px",
                              color: theme.palette.text.secondary,
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: "11px",
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {savedFilter.createdBy.fullName}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  }
                />
                <Box sx={{ display: "flex", gap: 0.5 }}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditFilter(savedFilter);
                    }}
                    sx={{
                      color: theme.palette.text.secondary,
                      "&:hover": {
                        color: theme.palette.primary.main,
                      },
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={(e) => handleDeleteFilter(savedFilter.id, e)}
                    sx={{
                      color: theme.palette.text.secondary,
                      "&:hover": {
                        color: theme.palette.error.main,
                      },
                    }}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Box>
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </Popover>
  );
};
